import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowRight, Copy, Info } from "lucide-react";
import { toast } from "sonner";

/**
 * Copy-and-roll dialog. Presents the user with:
 *  1. Checkbox list of scenarios to copy
 *  2. Target start year (default: latest known year + 1)
 *  3. EXPLICIT choice: which baseline? Resultaat Y-1 (historical) or Begroting Y (budget)
 *
 * Originals stay intact — copies get a " · {year}" suffix and can be edited.
 */
export default function RollForwardDialog({ open, onOpenChange, clubId, scenarios, historicals, budgets, onRolled }) {
  const t = useT();
  const [picked, setPicked] = useState(new Set());
  const [targetYear, setTargetYear] = useState(new Date().getFullYear() + 1);
  const [baselineSource, setBaselineSource] = useState("historical");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setPicked(new Set(scenarios?.map((s) => s.id) || []));
      const latestHist = (historicals || []).length ? Math.max(...historicals.map((h) => h.year)) : null;
      const latestBudget = (budgets || []).length ? Math.max(...budgets.map((b) => b.year)) : null;
      setTargetYear(Math.max(latestHist ? latestHist + 1 : 0, latestBudget || 0, new Date().getFullYear() + 1));
      // Prefer budget when one exists — that's the "spring loop" the treasurer uses.
      setBaselineSource(latestBudget ? "budget" : "historical");
    }
  }, [open, scenarios, historicals, budgets]);

  const toggle = (id) => setPicked((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const budgetForTarget = (budgets || []).find((b) => b.year === parseInt(targetYear, 10));
  const histForPrev = (historicals || []).find((h) => h.year === parseInt(targetYear, 10) - 1);

  // Auto-fall-back to historical baseline when the target-year budget disappears
  // (user changes target year to one without a budget). Prevents a confusing
  // "still selected but disabled" state.
  useEffect(() => {
    if (baselineSource === "budget" && !budgetForTarget) setBaselineSource("historical");
  }, [baselineSource, budgetForTarget]);

  const submit = async () => {
    if (picked.size === 0) { toast.error(t("Kies minstens één scenario om door te rollen.")); return; }
    if (baselineSource === "budget" && !budgetForTarget) {
      toast.error(t("Er is nog geen begroting voor {y}. Maak eerst de begroting aan, of kies 'Laatste jaarrekening'.").replace("{y}", String(targetYear)));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/clubs/${clubId}/scenarios/roll-forward`, {
        scenario_ids: Array.from(picked),
        new_start_year: parseInt(targetYear, 10),
        baseline_source: baselineSource,
      });
      toast.success(t("{n} scenario's doorgerold naar {y}").replace("{n}", String(data.created.length)).replace("{y}", String(targetYear)));
      onRolled?.(data.created);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="roll-forward-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl uppercase tracking-tight flex items-center gap-2 pr-8">
            <Copy className="w-5 h-5" /> {t("Scenario's doorrollen naar volgend jaar")}
          </DialogTitle>
          <DialogDescription>
            {t("Kopieert de gekozen scenario's en verschuift het startjaar. Originelen blijven bestaan — je kunt altijd terugvallen of vergelijken.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <div className="stat-label mb-2">{t("Scenario's om te kopiëren")}</div>
            <div className="border-2 border-foreground max-h-56 overflow-y-auto" data-testid="roll-scenario-list">
              {(scenarios || []).map((s) => (
                <label key={s.id} className="flex items-center gap-3 px-3 py-2 border-b border-foreground/10 last:border-b-0 hover:bg-muted/40 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={picked.has(s.id)}
                    onChange={() => toggle(s.id)}
                    data-testid={`roll-pick-${s.id}`}
                    className="w-4 h-4 border-2 border-foreground"
                  />
                  <div className="flex-1">
                    <div className="font-display font-black uppercase tracking-tight text-sm">{s.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {(s.kind || "base").toUpperCase()} · {t("Start")} {s.start_year} · {t("Horizon")} {s.horizon_years} {t("jr")}
                    </div>
                  </div>
                </label>
              ))}
              {(scenarios || []).length === 0 && (
                <div className="text-sm text-muted-foreground p-3">{t("Geen scenario's om te kopiëren.")}</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div>
              <div className="stat-label mb-1">{t("Nieuw startjaar")}</div>
              <input
                type="number"
                value={targetYear}
                onChange={(e) => setTargetYear(e.target.value)}
                data-testid="roll-target-year"
                className="border-2 border-foreground bg-background px-3 py-2 font-mono-num w-32 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <div className="stat-label mb-2">{t("Startpunt voor de nieuwe scenario's")}</div>
              <div className="space-y-2">
                <button
                  onClick={() => setBaselineSource("historical")}
                  data-testid="roll-baseline-historical"
                  className={`w-full text-left border-2 p-3 transition ${baselineSource === "historical" ? "border-foreground bg-primary/10" : "border-foreground/30 hover:border-foreground"}`}
                >
                  <div className="font-display font-black uppercase tracking-tight text-sm">
                    {t("Resultaat {y}").replace("{y}", String(parseInt(targetYear, 10) - 1))}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {histForPrev
                      ? t("Extrapolatie vanaf de jaarrekening (uit historicals).")
                      : t("Let op: er is nog geen jaarrekening voor {y}. Voeg 'm eerst toe.").replace("{y}", String(parseInt(targetYear, 10) - 1))}
                  </div>
                </button>
                <button
                  onClick={() => setBaselineSource("budget")}
                  disabled={!budgetForTarget}
                  data-testid="roll-baseline-budget"
                  className={`w-full text-left border-2 p-3 transition ${baselineSource === "budget" ? "border-foreground bg-primary/10" : "border-foreground/30 hover:border-foreground"} ${!budgetForTarget ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  <div className="font-display font-black uppercase tracking-tight text-sm">
                    {t("Begroting {y}").replace("{y}", String(targetYear))}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {budgetForTarget
                      ? t("Extrapolatie vanaf de begroting die je zojuist hebt aangemaakt.")
                      : t("Er is nog geen begroting voor {y}. Maak 'm eerst aan op de Begroting-tab.").replace("{y}", String(targetYear))}
                  </div>
                </button>
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 p-3 border-l-4 border-primary">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{t("Aannames voor groei, inflatie, investeringen en reserves blijven onveranderd — je rolt alleen het startjaar door.")}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <button onClick={() => onOpenChange(false)} disabled={saving}
            data-testid="roll-cancel"
            className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold">
            {t("Annuleren")}
          </button>
          <button onClick={submit} disabled={saving || picked.size === 0}
            data-testid="roll-submit"
            className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold inline-flex items-center gap-2 disabled:opacity-40">
            {saving ? t("Bezig…") : (<><Copy className="w-4 h-4" /> {t("Kopieer en rol door")} <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
